import clsx from 'clsx'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import useBundleGlobal from 'hooks/useBundleGlobal'
import {
  fetchProductByIds,
  fetchRecommendedProductById
} from 'libs/shopify-product'
import normalizeProducts from 'libs/shopify-normalize'
import { removeClass } from 'helpers/dom'
import { fetchProductByAjax } from 'helpers/utils'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import CarouselTabs from 'snippets/carousel-tabs/carousel-tabs'

const selectors = {
  HIDDEN_CLASS: 'hidden',
  DEFAULT_OPTIONS: {
    slidesPerView: 1
  },
  LIMIT_PRODUCTS: 6
}

function ProductUpsellCarousel() {
  const { headingUpsellBundles, headingUpsellRecommendations, initProduct } =
    window.productState || {}
  const { id: productId, initVariantId } = initProduct || {}
  const tabs = [headingUpsellBundles, headingUpsellRecommendations]
  const { productBundles, isLoadingBundles } = useBundleGlobal()

  const [productConfig, setProductConfig] = useState({
    [headingUpsellBundles]: [],
    [headingUpsellRecommendations]: []
  })
  const [selectedTab, setSelectedTab] = useState(headingUpsellBundles)
  const [isLoading, setIsLoading] = useState(false)
  const refProductTab = useRef(null)

  const products = useMemo(() => {
    const list = productConfig[selectedTab] || []

    return list.filter(
      (product) => product?.metafields?.seo_hidden !== '1'
    )
  }, [selectedTab, productConfig])

  const productBundlesParentIds = useMemo(() => productBundles
      .filter((product) =>
        product.bundlesChild?.some(
          (bundle) => bundle.variant_id === initVariantId
        )
      )
      .map((product) => product.id.toString()), [productBundles, initVariantId])

  const getProductsRecommended = async () => {
    if (!productId) return []

    const recommendations = await fetchRecommendedProductById(productId)

    if (!recommendations?.length) return []

    return normalizeProducts(recommendations, 'shopify-graphql-recommendations')
        .filter((product) => product.metafields?.seo_hidden !== '1')
        .slice(0, selectors.LIMIT_PRODUCTS)
  }

  const getProductsByStorefront = async (ids) => {
    if (!ids?.length) return []

    const response = await fetchProductByIds(ids)

    if (!response?.length) return []

    const handles = response.map((product) => product.handle)
    const productList = await fetchProductByAjax(handles)

    return productList
  }

  const init = async () => {
    try {
      setIsLoading(true)

      const [bundlesData, recommendationsData] = await Promise.all([
        getProductsByStorefront(productBundlesParentIds),
        getProductsRecommended()
      ])

      setProductConfig({
        [headingUpsellBundles]: bundlesData,
        [headingUpsellRecommendations]: recommendationsData
      })

      if ((bundlesData.length || recommendationsData.length) && refProductTab.current) {
        removeClass(selectors.HIDDEN_CLASS, refProductTab.current)

        if (!bundlesData.length) {
          setSelectedTab(headingUpsellRecommendations)
        }
      }
    } catch (error) {
      console.error('[ProductUpsellCarousel]', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!refProductTab.current) return

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !isLoadingBundles) {
        init()
        observer.disconnect()
      }
    })

    observer.observe(refProductTab.current)

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect()
  }, [isLoadingBundles])

  return (
    <>
      {isLoading && (
        <div className="mt-10 flex w-full items-center justify-center md:mt-8">
          <LoaderSpin size="8" />
        </div>
      )}
      <CarouselTabs
        tabs={tabs}
        products={products}
        productConfig={productConfig}
        options={selectors.DEFAULT_OPTIONS}
        selectedTab={selectedTab}
        hasUnderBorder
        setSelectedTab={setSelectedTab}
        refProductTab={refProductTab}
        classCarouselWrapper={clsx('carousel-product-upsell', isLoading && selectors.HIDDEN_CLASS)}
        isCustomProductCard
        isHeadingNative={false}
        classCarouselHeader="md-max:w-[calc(100%_-_100px)]"
      />

      <div className="absolute left-0 bottom-0 w-full h-px bg-neutral-900 z-3" />
    </>
  )
}

export default ProductUpsellCarousel
